import React from 'react';
import { string } from 'prop-types';

import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';
import {
  categorySlug,
  findCategoryBySlug,
  subcategoriesOf,
} from '../../../util/categoryLinks';

import css from './PopularModels.module.css';

/**
 * "Populära modeller" — a row of links to the models of the brand being viewed.
 *
 * A brand page had no way through to its model pages: the footer links brands
 * only, so a shopper on the Volvo page could not narrow to an XC60 without going
 * back to search, and a crawler reached the model pages through the hub page
 * alone. This row gives every model page a second, closer link.
 *
 * Renders nothing unless a brand is being viewed on its own — with a model
 * already chosen the row would only link sideways, and on the unfiltered search
 * page there is no brand to list models for.
 *
 * The links come from the hosted category configuration, so they cost no query
 * and are present in the server-rendered HTML.
 */
const PopularModels = props => {
  const { make, model, className } = props;
  const config = useConfiguration();

  if (!make || model) {
    return null;
  }

  const brand = findCategoryBySlug(config.categoryConfiguration?.categories, make);
  const models = subcategoriesOf(brand);

  if (!brand || models.length === 0) {
    return null;
  }

  return (
    <nav className={className || css.root} aria-label={`Modeller för ${brand.name}`}>
      <h2 className={css.heading}>Populära modeller</h2>
      <div className={css.links}>
        {models.map((modelCategory, index) => (
          <span key={modelCategory.id} className={css.item}>
            <NamedLink
              name="SearchPageMakeModel"
              params={{ make: categorySlug(brand), model: categorySlug(modelCategory) }}
              className={css.link}
            >
              {`${brand.name} ${modelCategory.name}`}
            </NamedLink>
            {index < models.length - 1 ? <span className={css.sep}>·</span> : null}
          </span>
        ))}
      </div>
    </nav>
  );
};

PopularModels.defaultProps = {
  make: null,
  model: null,
  className: null,
};

PopularModels.propTypes = {
  // Both come straight from the URL, so they are slugs, not display names.
  make: string,
  model: string,
  className: string,
};

export default PopularModels;
